import {
  STORE_KEY_ACCOUNT_DATA_RETENTION,
  storeGet,
} from 'mailup-react-helpers'
import {
  type UiFilterCondition,
  type UiActivityConditionType,
  type UiActivityMessageLinkSpec,
  type UiActivityMessageSpec,
  type UiFilterDetail,
  UiActivityDateType,
  type UiActivitySpec,
  type UiActivityDateValue,
  type UiActivitySubsSpec,
  type UiGenericSpec,
  type UiActivityDateRangeValue,
} from '../filterConditionTypes'
import { activityDefaultMsgValues } from './conditionOptions'
import { EmailMatch } from '../../../types/FilterCondition'
import { type IntlShape } from 'react-intl'

export const validateUiActivityMessageSpec = (
  cond: UiFilterCondition,
  filter: UiFilterDetail,
): boolean => {
  const spec = cond.Spec as UiActivityMessageSpec
  return (
    validateActivityDate(cond) &&
    validateMessageRequired(spec) &&
    validateMessageValue(spec, filter) &&
    validateTagsValue(spec, filter)
  )
}
export const validateMessageRequired = (
  spec: UiActivityMessageSpec,
): boolean => {
  return spec.Message !== undefined
}
export const validateMessageValue = (
  spec: UiActivityMessageSpec,
  filter: UiFilterDetail,
): boolean => {
  return !(
    filter.IdList === 0 &&
    !activityDefaultMsgValues.some((m) => m === spec.Message)
  )
}
export const validateTagsValue = (
  spec: UiActivityMessageSpec,
  filter: UiFilterDetail,
): boolean => {
  return !(filter.IdList === 0 && spec.Tags?.length > 0)
}

export const validateUiActivityMessageLinkSpec = (
  cond: UiFilterCondition,
  filter: UiFilterDetail,
): boolean => {
  const spec = cond.Spec as UiActivityMessageLinkSpec
  return (
    validateUiActivityMessageSpec(cond, filter) &&
    validateLinkValue(spec, filter)
  )
}
export const validateLinkValue = (
  spec: UiActivityMessageLinkSpec,
  filter: UiFilterDetail,
): boolean => {
  return !(filter.IdList === 0 && spec.Link?.length > 0)
}

export const validateActivityDate = (cond: UiFilterCondition): boolean => {
  return validateActivityDateValue(cond) && validateActivityDateRetention(cond)
}

const validateActivityDateValue = (cond: UiFilterCondition): boolean => {
  const spec = cond.Spec as UiActivitySpec
  switch (spec.DateType) {
    case UiActivityDateType.AnyMoment:
      return spec.DateValue === null
    case UiActivityDateType.ExactDaysAgo:
    case UiActivityDateType.LastDays:
    case UiActivityDateType.ExactHoursAgo:
    case UiActivityDateType.LastHours:
      return validateActivityDateInteger(spec.DateValue)
    case UiActivityDateType.DateInterval:
      return validateActivityDateInterval(spec.DateValue)
    default:
      return false
  }
}
const validateActivityDateInteger = (value: UiActivityDateValue): boolean => {
  return typeof value === 'number' && Number.isInteger(value) && value >= 0
}
const validateActivityDateInterval = (value: UiActivityDateValue): boolean => {
  return (
    isShapeOfActivityDateInterval(value) &&
    ((value as UiActivityDateRangeValue).To !== null ||
      (value as UiActivityDateRangeValue).From !== null)
  )
}
const isShapeOfActivityDateInterval = (value: UiActivityDateValue): boolean => {
  return (
    typeof value === 'object' &&
    value !== null &&
    !Array.isArray(value) &&
    ('To' in value || 'From' in value)
  )
}

const validateActivityDateRetention = (cond: UiFilterCondition): boolean => {
  const spec = cond.Spec as UiActivitySpec
  const retention = getDataRetentionLimitForSpec(cond)
  if (retention !== undefined) {
    if (typeof spec.DateValue === 'number') {
      return spec.DateValue <= retention
    }
    return false
  }
  return true
}
/** 
 return a number representing days or hours based on UiActivityConditionType and UiActivityDateType. 
 return undefined in case the limit doesn't need to be considered for validation
 */
const getDataRetentionLimitForSpec = (
  cond: UiFilterCondition,
): number | undefined => {
  const retentionDays = getDataRentionDays(
    cond.ConditionType as UiActivityConditionType,
  )
  const spec = cond.Spec as UiActivitySpec
  switch (spec.DateType) {
    case UiActivityDateType.ExactDaysAgo:
      return retentionDays - 1
    case UiActivityDateType.LastDays:
      return retentionDays

    case UiActivityDateType.ExactHoursAgo:
      return retentionDays * 24 - 1
    case UiActivityDateType.LastHours:
      return retentionDays * 24

    default:
      return undefined
  }
}
/** use this function instead of getActivityDataRetentionDaysUncapped and getTraceDataRetentionDaysCapped unless you're really what you're doing */
export const getDataRentionDays = (
  condType?: UiActivityConditionType,
): number => {
  if (
    condType === EmailMatch.EmailSent ||
    condType === EmailMatch.EmailNotSent ||
    condType === EmailMatch.EmailSuccessfullySent
  ) {
    return getTraceDataRetentionDaysCapped()
  } else {
    return getActivityDataRetentionDaysUncapped()
  }
}
/** use getDataRentionDays(conditionType) function instead of this unless you're sure what you're doing */
export const getActivityDataRetentionDaysUncapped = (): number => {
  return storeGet(STORE_KEY_ACCOUNT_DATA_RETENTION) as number
}
/** use getDataRentionDays(conditionType) function instead of this unless you're sure what you're doing */
export const getTraceDataRetentionDaysCapped = (): number => {
  const accountDataRetention = storeGet(
    STORE_KEY_ACCOUNT_DATA_RETENTION,
  ) as number
  return Math.min(365, accountDataRetention)
}

export const activityDateErrorMessageId = (
  cond: UiFilterCondition,
  intl: IntlShape,
): string | undefined => {
  if (!validateActivityDateValue(cond)) {
    return intl.formatMessage({
      id: 'Load.Filters.ActivityDateValueErrorNumber',
    })
  }
  if (!validateActivityDateRetention(cond)) {
    const retentionLimit = getDataRetentionLimitForSpec(cond)
    const spec = cond.Spec as UiActivitySpec
    switch (spec.DateType) {
      case UiActivityDateType.ExactHoursAgo:
      case UiActivityDateType.LastHours:
        return intl.formatMessage(
          { id: 'Load.Filters.ActivityDateValueErrorHoursRetention' },
          { retention: retentionLimit },
        )
      default:
        return intl.formatMessage(
          { id: 'Load.Filters.ActivityDateValueErrorDaysRetention' },
          { retention: retentionLimit },
        )
    }
  }
  return undefined
}

export function isUiActivitySubsSpec(
  _uiSpec: Partial<UiGenericSpec>,
  uiCond: UiFilterCondition,
): _uiSpec is UiActivitySubsSpec {
  return validateActivityDate(uiCond)
}

export function isUiActivityDateRangeValue(
  value: UiActivityDateValue,
): value is UiActivityDateRangeValue {
  return isShapeOfActivityDateInterval(value)
}

export function isUiActivityMessageSpec(
  _uiSpec: Partial<UiGenericSpec>,
  uiCond: UiFilterCondition,
  f: UiFilterDetail,
): _uiSpec is UiActivityMessageSpec {
  return validateUiActivityMessageSpec(uiCond, f)
}

export function isUiActivityMessageLinkSpec(
  _uiSpec: Partial<UiGenericSpec>,
  uiCond: UiFilterCondition,
  f: UiFilterDetail,
): _uiSpec is UiActivityMessageLinkSpec {
  return validateUiActivityMessageLinkSpec(uiCond, f)
}
